import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, Button, InputAdornment, TextField, Typography, useTheme } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { categoryHeader, emptyResults, modalActions, recipeCheckIcon, recipeItem, recipeListContainer, recipeTitle, searchBarContainer, searchIcon } from "../../styles/recipeSelectModalStyles";
import PrimaryModal from "./PrimaryModal";
import { filterAndSortRecipes } from "./utils";

const RecipeSelectModal = ({ open, handleClose, recipes, markedRecipes, handleSave }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRecipeIds, setSelectedRecipeIds] = useState([]);

  const debounceSearch = useCallback((value) => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const cleanup = debounceSearch(searchInput);
    return cleanup;
  }, [searchInput, debounceSearch]);

  // Reset selections when modal opens
  useEffect(() => {
    if (open) {
      setSearchInput("");
      setSearchTerm("");
      setSelectedRecipeIds([]);
    }
  }, [open]);

  const filteredMarkedRecipes = useMemo(() => filterAndSortRecipes(markedRecipes, searchTerm), [markedRecipes, searchTerm]);

  const filteredRegularRecipes = useMemo(() => filterAndSortRecipes(recipes, searchTerm), [recipes, searchTerm]);

  const handleRecipeSelect = useCallback((recipeId) => {
    setSelectedRecipeIds((prevSelected) => {
      if (prevSelected.includes(recipeId)) {
        return prevSelected.filter((id) => id !== recipeId);
      } else {
        return [...prevSelected, recipeId];
      }
    });
  }, []);

  const handleConfirmSelection = useCallback(() => {
    if (selectedRecipeIds.length > 0) {
      handleSave(selectedRecipeIds);
    }
  }, [handleSave, selectedRecipeIds]);

  const handleClearSearch = useCallback(() => {
    setSearchInput("");
    setSearchTerm("");
  }, []);

  // Determine if there are any results to show
  const hasResults = filteredMarkedRecipes.length > 0 || filteredRegularRecipes.length > 0;

  return (
    <PrimaryModal open={open} handleClose={handleClose} title={t("selectRecipeTitle")}>
      {/* Search Bar */}
      <Box sx={searchBarContainer(theme)}>
        <TextField
          fullWidth
          placeholder={t("searchRecipes")}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          variant="outlined"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  {searchInput ? <ClearIcon sx={searchIcon(theme)} onClick={handleClearSearch} style={{ cursor: "pointer" }} /> : <SearchIcon sx={searchIcon(theme)} />}
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {/* Recipe List */}
      <Box sx={recipeListContainer(theme)}>
        {!hasResults && <Typography sx={emptyResults(theme)}>{t("recipeNotFound")}</Typography>}

        {filteredMarkedRecipes.length > 0 && (
          <>
            <Typography sx={categoryHeader(theme)}>{t("markedRecipes")}</Typography>
            {filteredMarkedRecipes.map((recipe) => (
              <Box key={recipe.id} sx={recipeItem(theme, selectedRecipeIds.includes(recipe.id))} onClick={() => handleRecipeSelect(recipe.id)}>
                <Typography sx={recipeTitle(theme)}>{recipe.title}</Typography>
                {selectedRecipeIds.includes(recipe.id) ? <CheckCircleIcon sx={recipeCheckIcon(theme, true)} /> : <RadioButtonUncheckedIcon sx={recipeCheckIcon(theme, false)} />}
              </Box>
            ))}
          </>
        )}

        {filteredRegularRecipes.length > 0 && (
          <>
            <Typography sx={categoryHeader(theme)}>{filteredMarkedRecipes.length > 0 ? t("allRecipes") : t("recipes")}</Typography>
            {filteredRegularRecipes.map((recipe) => (
              <Box key={recipe.id} sx={recipeItem(theme, selectedRecipeIds.includes(recipe.id))} onClick={() => handleRecipeSelect(recipe.id)}>
                <Typography sx={recipeTitle(theme)}>{recipe.title}</Typography>
                {selectedRecipeIds.includes(recipe.id) ? <CheckCircleIcon sx={recipeCheckIcon(theme, true)} /> : <RadioButtonUncheckedIcon sx={recipeCheckIcon(theme, false)} />}
              </Box>
            ))}
          </>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={modalActions(theme)}>
        <Button onClick={handleClose} color="secondary">
          {t("cancel")}
        </Button>
        <Button onClick={handleConfirmSelection} variant="contained" color="primary" disabled={selectedRecipeIds.length === 0}>
          {t("add")} {selectedRecipeIds.length > 0 && `(${selectedRecipeIds.length})`}
        </Button>
      </Box>
    </PrimaryModal>
  );
};

export default RecipeSelectModal;
